import { type TariffKey } from '@/pages/finance/components/TariffManagement/AdvertisingTooltip';
import { pluralizeRu } from '@/pages/finance/utils/pluralize-ru.ts';
import { type AbPackage, type AbPackageCount } from '@/types/types';
import { financeUtil } from '@marpla/core/util/finance.util';

import {
  AB_PACKAGE_TEST_COUNT,
  AB_TESTS_COSTS_WITHOUT_TARIFF,
  AB_TESTS_COSTS_WITH_TARIFF,
  type CostItem,
  PERIOD_AB_TEST_COUNT_FREE,
  PERIOD_AB_TEST_COUNT_PAID,
  PHRASES_COST,
  PROJECT_COST,
  type ProjectCostItem,
} from './Tariffs';


export enum Tariffs {
  NEWBIE = 'newbie',
  DEFAULT = 'DEFAULT',
  PAID = 'paid',
  PROMOCODE = 'promocode',
  LIMITED = 'limited',
}

export enum TariffTypes {
  FREE = 'free',
  DEFAULT = 'default',
  PAID = 'paid',
}

export enum TariffDisplayNames {
  NEWBIE = 'Новичок',
  DEFAULT = 'Не оплачен',
  PAID = 'Платный',
  PROMOCODE = 'Промокод',
  LIMITED = 'Ограниченный',
}

export interface Tariff {
  id: string | number;
  name: Tariffs;
  type: TariffTypes;
  displayName: TariffDisplayNames;
  info: string;
  price_per_day: number;
  base_price: number;
  additional_services_price: number;
  project_limits: number;
  features: {
    seo: boolean;
    advertising: boolean;
    sales: boolean;
    ab_tests: number;
    max_phrases: number;
    max_projects: number;
    max_stores_advertising: number;
    max_stores_sales: number;
  };
  phrasesCosts?: CostItem[];
  abTestsCosts?: CostItem[];
  abTestsCostsWithoutTariff?: CostItem[];
  projectsCosts?: ProjectCostItem[];
  conditions: string;
  is_free: boolean;
  is_active: boolean;
  /** whether tariff is visible to user */
  visible: boolean;
}

export const DEFAULT_TARIFFS: Record<Tariffs, Tariff> = {
  [Tariffs.DEFAULT]: {
    id: Tariffs.DEFAULT,
    name: Tariffs.DEFAULT,
    type: TariffTypes.DEFAULT,
    displayName: TariffDisplayNames.DEFAULT,
    info: '',
    price_per_day: 0,
    base_price: 0,
    additional_services_price: 50,
    project_limits: 1,
    features: {
      seo: true,
      advertising: true,
      sales: true,
      ab_tests: 0,
      max_phrases: 0,
      max_projects: 0,
      max_stores_advertising: 0,
      max_stores_sales: 0,
    },
    conditions: '',
    is_free: true,
    is_active: false,
    abTestsCosts: AB_TESTS_COSTS_WITH_TARIFF,
    abTestsCostsWithoutTariff: AB_TESTS_COSTS_WITHOUT_TARIFF,
    projectsCosts: PROJECT_COST,
    phrasesCosts: PHRASES_COST,

    visible: true,
  },
  [Tariffs.PAID]: {
    id: Tariffs.PAID,
    name: Tariffs.PAID,
    type: TariffTypes.PAID,
    displayName: TariffDisplayNames.PAID,
    info: 'Все возможности MARPLA без ограничений',
    price_per_day: 217,
    base_price: 217,
    additional_services_price: 50,
    project_limits: 5,
    features: {
      seo: true,
      advertising: true,
      sales: true,
      ab_tests: 5,
      max_phrases: 5000,
      max_projects: 5,
      max_stores_advertising: 1,
      max_stores_sales: 1,
    },
    conditions: 'От 217 ₽ / день',
    is_free: false,
    is_active: false,
    abTestsCosts: AB_TESTS_COSTS_WITH_TARIFF,
    abTestsCostsWithoutTariff: AB_TESTS_COSTS_WITHOUT_TARIFF,
    projectsCosts: PROJECT_COST,
    phrasesCosts: PHRASES_COST,
    visible: true,
  },
  [Tariffs.NEWBIE]: {
    id: Tariffs.NEWBIE,
    name: Tariffs.NEWBIE,
    type: TariffTypes.FREE,
    displayName: TariffDisplayNames.NEWBIE,
    info: 'Тариф для новых пользователей в первые 7 дней',
    price_per_day: 0,
    base_price: 0,
    additional_services_price: 0,
    project_limits: 1,
    features: {
      seo: true,
      advertising: false,
      sales: false,
      ab_tests: 2,
      max_phrases: 500,
      max_projects: 1,
      max_stores_advertising: 1,
      max_stores_sales: 1,
    },
    conditions: 'Первые 7 дней после регистрации',
    is_free: true,
    is_active: false,
    abTestsCosts: AB_TESTS_COSTS_WITH_TARIFF,
    abTestsCostsWithoutTariff: AB_TESTS_COSTS_WITHOUT_TARIFF,
    projectsCosts: PROJECT_COST,
    phrasesCosts: PHRASES_COST,
    visible: false,
  },
  [Tariffs.PROMOCODE]: {
    id: Tariffs.PROMOCODE,
    name: Tariffs.PROMOCODE,
    type: TariffTypes.FREE,
    displayName: TariffDisplayNames.PROMOCODE,
    info: 'Расширенный бесплатный тариф по промокоду',
    price_per_day: 0,
    base_price: 0,
    additional_services_price: 0,
    project_limits: 5,
    features: {
      seo: true,
      advertising: true,
      sales: true,
      ab_tests: 5,
      max_phrases: 2000,
      max_projects: 5,
      max_stores_advertising: 2,
      max_stores_sales: 2,
    },
    conditions: 'Активация промокода',
    is_free: true,
    is_active: false,
    abTestsCosts: AB_TESTS_COSTS_WITH_TARIFF,
    abTestsCostsWithoutTariff: AB_TESTS_COSTS_WITHOUT_TARIFF,
    projectsCosts: PROJECT_COST,
    phrasesCosts: PHRASES_COST,
    visible: false,
  },
  [Tariffs.LIMITED]: {
    id: Tariffs.LIMITED,
    name: Tariffs.LIMITED,
    type: TariffTypes.PAID,
    displayName: TariffDisplayNames.LIMITED,
    info: 'Ограниченный тариф при превышении лимитов',
    price_per_day: 0,
    base_price: 0,
    additional_services_price: 0,
    project_limits: 0,
    features: {
      seo: false,
      advertising: false,
      sales: false,
      ab_tests: 0,
      max_phrases: 0,
      max_projects: 0,
      max_stores_advertising: 0,
      max_stores_sales: 0,
    },
    conditions: 'Превышение лимитов бесплатного тарифа',
    is_free: true,
    is_active: false,
    abTestsCosts: AB_TESTS_COSTS_WITH_TARIFF,
    abTestsCostsWithoutTariff: AB_TESTS_COSTS_WITHOUT_TARIFF,
    projectsCosts: PROJECT_COST,
    phrasesCosts: PHRASES_COST,
    visible: false,
  },
};

export const SEO_TARIFF_TOOLTIPS: Record<string, string> = {
  free: 'Бесплатный тариф включает 500 фраз и 1 проект',
  paid: 'Базовый тариф включает 5 000 фраз и 5 проектов',
};

export const SEO_TARIFF_LIMITS: Record<
  string,
  { includedPhrases: number; includedProjects: number }
> = {
  free: { includedPhrases: 500, includedProjects: 1 },
  paid: { includedPhrases: 5000, includedProjects: 5 },
};

const PHRASE_PRICE_TIERS = [
  { maxLimit: 8000, pricePerDay: 20 },
  { maxLimit: 10000, pricePerDay: 80 },
  { maxLimit: 15000, pricePerDay: 120 },
  { maxLimit: 20000, pricePerDay: 220 },
  { maxLimit: 30000, pricePerDay: 400 },
  { maxLimit: 50000, pricePerDay: 700 },
  { maxLimit: Infinity, pricePerDay: 1466 },
];

export const calcPhrasesExtraPerDay = (
  currentPhrases: number,
  includedPhrases: number,
): { extraCost: number; nextLimitLabel: string } => {
  if (currentPhrases <= includedPhrases) {
    return { extraCost: 0, nextLimitLabel: `До ${includedPhrases.toLocaleString('ru-RU')}` };
  }

  const matchingTier = PHRASE_PRICE_TIERS.find((tier) => currentPhrases <= tier.maxLimit)!;
  const nextLimitLabel =
    matchingTier.maxLimit === Infinity
      ? 'Безлимит'
      : `До ${matchingTier.maxLimit.toLocaleString('ru-RU')}`;

  return { extraCost: matchingTier.pricePerDay, nextLimitLabel };
};

export const calcProjectsExtraPerDay = (
  currentProjects: number,
  includedProjects: number,
): {
  extraCost: number;
  breakdown: {
    tierName: string;
    blockCount: number;
    subtotal: number;
  }[];
} => {
  const overLimit = Math.max(0, currentProjects - includedProjects);

  if (overLimit === 0) {
    return {
      extraCost: 0,
      breakdown: [] as { tierName: string; blockCount: number; subtotal: number }[],
    };
  }

  let remainingProjects = overLimit;
  const breakdown: { tierName: string; blockCount: number; subtotal: number }[] = [];

  const PROJECT_PRICE_SEGMENTS = [
    { tierName: '6–20', maxProjects: 15, pricePerBlock: 66 },
    { tierName: '21–40', maxProjects: 20, pricePerBlock: 33 },
    { tierName: '41+', maxProjects: Infinity, pricePerBlock: 17 },
  ];

  let totalExtraCost = 0;

  for (const segment of PROJECT_PRICE_SEGMENTS) {
    if (remainingProjects <= 0) break;

    const projectsInSegment = Math.min(remainingProjects, segment.maxProjects);
    const blockCount = Math.ceil(projectsInSegment / 5);
    const subtotal = blockCount * segment.pricePerBlock;

    breakdown.push({ tierName: segment.tierName, blockCount, subtotal });

    totalExtraCost += subtotal;
    remainingProjects -= projectsInSegment;
  }

  return { extraCost: totalExtraCost, breakdown };
};

export const formatNumber = (n: number | string): string =>
  isNumber(n) ? n.toLocaleString('ru-RU') : String(n);

export const INCLUDED_STORES_COUNT = 1;
export const EXTRA_PER_STORE = 50;

export const AUTO_BUY_ENABLED_TEXT =
  'Когда остаток тестов закончится и вы начнёте новый, стоимость доп. пакета спишется с баланса.';

export const AUTO_BUY_DISABLED_TEXT =
  'Включите автопокупку: когда тесты закончатся и вы начнёте новый, стоимость пакета спишется с баланса. Отказаться можно в любой момент.';

export const TITLE_ABTESTS_AVAILABLE = 'Купить A/B-тесты';
export const TITLE_ABTESTS_UNAVAILABLE = 'Покупка новых тестов пока недоступна';
export const TEXT_ABTESTS_AUTOBUY_ENABLED =
  'Когда бесплатные тесты закончатся и начнётся новый пакет, стоимость спишется с баланса.';
export const TEXT_ABTESTS_AUTOBUY_DISABLED =
  'Автопокупка больше не будет срабатывать автоматически.';
export const TOOLTIP_ABTESTS_AUTOBUY =
  'Если включить автопокупку, система будет автоматически приобретать новый пакет A/B-тестов, когда текущие тесты закончатся. Вы можете в любой момент отключить автопокупку — новые пакеты тогда не будут покупаться автоматически.';

export const basePrice = DEFAULT_TARIFFS.paid.base_price;

export const UNLIMITED_VALUE = 300;
export const UNLIMITED_LABEL = 'Безлимит';

const ADVERTISING_PRICING_TEXTS: Record<TariffKey, string> = {
  default: `Один артикул включён в ваш тариф. При превышении — переведем на тариф «${DEFAULT_TARIFFS.paid.displayName}»: ${DEFAULT_TARIFFS.paid.base_price} ₽/день + ${EXTRA_PER_STORE} ₽/день за каждый магазин, начиная со второго.`,
  promocode: `Один артикул включён в ваш тариф. При превышении — переведем на тариф «${DEFAULT_TARIFFS.paid.displayName}»: ${DEFAULT_TARIFFS.paid.base_price} ₽/день + ${EXTRA_PER_STORE} ₽/день за каждый магазин, начиная со второго.`,
  paid: `Один магазин входит в Базовый тариф, каждый следующий — +${EXTRA_PER_STORE} ₽ / день`,
};

export const getAdvertisingPricingInfo = (tariffKey: TariffKey): string =>
  ADVERTISING_PRICING_TEXTS[tariffKey] ?? '';

const SALES_PRICING_TEXTS: Record<TariffKey, string> = {
  default: `Один магазин включён в ваш тариф. При превышении — переведем на тариф «${DEFAULT_TARIFFS.paid.displayName}»: ${DEFAULT_TARIFFS.paid.base_price} ₽/день + ${EXTRA_PER_STORE} ₽/день за каждый магазин, начиная со второго.`,
  promocode: `Один магазин включён в ваш тариф. При превышении — переведем на тариф «${DEFAULT_TARIFFS.paid.displayName}»: ${DEFAULT_TARIFFS.paid.base_price} ₽/день + ${EXTRA_PER_STORE} ₽/день за каждый магазин, начиная со второго.`,
  paid: `Один магазин входит в Базовый тариф, каждый следующий — +${EXTRA_PER_STORE}  ₽ / день`,
};

export const getSalesPricingInfo = (tariffKey: TariffKey): string =>
  SALES_PRICING_TEXTS[tariffKey] ?? '';

export const adaptPackagesToCosts = (packages: AbPackage['packagesWithTariff']): CostItem[] => {
  if (!packages?.length) return [];

  // Map local package shape to API-like shape expected by core util
  const apiLike = packages.map((p) => ({
    range: p.range,
    price: p.price,
    is_current: p.isCurrent,
  }));

  return financeUtil.adaptPackagesToCosts(
    apiLike as any,
    AB_PACKAGE_TEST_COUNT,
  ) as unknown as CostItem[];
};

export type AbPackageType = NonNullable<AbPackageCount['type']>;

export const TYPE_LABEL: Record<AbPackageType, string> = {
  deposit: 'пополнение',
  paid: 'ваша покупка',
  free: 'бесплатные',
};

export const TYPE_WEIGHT: Record<AbPackageType, number> = {
  paid: 0,
  free: 1,
  deposit: 2,
};

const makeFreeTestsText = (count: number): string =>
  count
    ? `В начале каждого периода вы получите ${count} ${pluralizeRu(
        'test',
        count,
      )} бесплатно, далее по ценам в таблице 👇🏻`
    : '';

export const FREE_TESTS_TEXT: Record<string, { with_tariff: string; without_tariff: string }> = {
  [Tariffs.PAID]: {
    with_tariff: makeFreeTestsText(PERIOD_AB_TEST_COUNT_PAID),
    without_tariff: makeFreeTestsText(PERIOD_AB_TEST_COUNT_FREE),
  },
  default: {
    with_tariff: makeFreeTestsText(PERIOD_AB_TEST_COUNT_PAID),
    without_tariff: makeFreeTestsText(PERIOD_AB_TEST_COUNT_FREE),
  },
};

export const PROJECTS_EXTRA_THRESHOLD = 6;
